import { WebDavServerModel } from "client/Api/WebDavSettings/webDavSettings";
import { useMemo, useState } from "react";

export type FolderNavigationStackItem = {
	readonly id: string;
	readonly parentId: string | null;
	readonly name: string;
};

export type FolderNavigationStack = {
	items: readonly FolderNavigationStackItem[];
	reset(): void;
	set(items: FolderNavigationStackItem[]): void;
	navigateForwardTo(item: FolderNavigationStackItem): void;
	navigateBackTo(folderId: string): void;
};

export function useFolderNavigationStack(): FolderNavigationStack {
	const [items, setItems] = useState<readonly FolderNavigationStackItem[]>([]);

	return useMemo(
		(): FolderNavigationStack => {
			return {
				items: items,
				reset: () => setItems([]),
				set: (items: readonly FolderNavigationStackItem[]) => setItems(items),
				navigateForwardTo: (item: FolderNavigationStackItem): void => {
					setItems(
						(current) => {
							const copy: FolderNavigationStackItem[] = [...current];
							const currentFolderId: string | undefined = copy[copy.length - 1]
								?.id;

							if (item.parentId !== currentFolderId) {
								throw new Error("The folder is not a child of the current folder");
							}

							copy.push(item);

							return copy;
						}
					);
				},
				navigateBackTo: (folderId: string): void => {
					setItems(
						(current) => {
							const copy: FolderNavigationStackItem[] = [...current];

							const index: number = copy.findIndex(x => x.id === folderId);

							if (index === -1) {
								throw new Error("Folder is not present on the stack");
							}

							const length: number = copy.length;
							if (index === length) {
								throw new Error("Can't navigate back to the current folder on the stack");
							}

							copy.splice(index + 1);

							return copy;
						}
					);
				}
			};
		},
		[items]
	);
};

export const RootFolderId = "/";

export function createRootFolder(server: WebDavServerModel): FolderNavigationStackItem {
	return {
		id: RootFolderId,
		parentId: null,
		name: server.name,
	};
}